import type { TuningIdentity } from '../contracts/identity';
import type { HostStatsFeedOptions, HostStatsSample, IHostStatsFeed } from './HostTelemetryContracts';

/** One native read owner; production gets its own one-second reduction boundary. */
export class HostStatsFeed implements IHostStatsFeed {
  private readonly listeners = new Set<(sample: HostStatsSample) => void>();
  private target: TuningIdentity | undefined;
  private context = { endpointConnectionEpoch: 0, captureGeneration: 0 };
  private interval = 1_000;
  private timer: ReturnType<typeof setInterval> | undefined;
  private productionAt: number | undefined;
  private skipped = 0;
  private generation = 0;
  private inFlight = false;
  private running = false;

  constructor(private readonly options: HostStatsFeedOptions) {}

  configure(target: TuningIdentity, intervalMs: 250 | 500 | 1000 | null,
    context: { endpointConnectionEpoch: number; captureGeneration: number }): void {
    const changed = !this.target || target.connectionEpoch !== this.target.connectionEpoch
      || target.trialId !== this.target.trialId || context.endpointConnectionEpoch !== this.context.endpointConnectionEpoch;
    if (this.target && !changed && (target.configRevision !== this.target.configRevision || context.captureGeneration !== this.context.captureGeneration)) this.generation++;
    this.target = { ...target }; this.context = { ...context };
    if (changed) { this.generation++; this.productionAt = undefined; this.skipped = 0; }
    const interval = intervalMs || 1_000;
    if (this.interval !== interval) { this.interval = interval; if (this.running) this.schedule(); }
  }

  subscribe(listener: (sample: HostStatsSample) => void): () => void {
    this.listeners.add(listener); return () => this.listeners.delete(listener);
  }
  start(): void { if (!this.running) { this.running = true; this.schedule(); void this.poll(); } }
  stop(): void { this.running = false; this.generation++; if (this.timer) clearInterval(this.timer); this.timer = undefined; }
  dispose(): void { this.stop(); this.listeners.clear(); }

  async poll(): Promise<void> {
    if (!this.running || !this.target || !this.options.isCurrent()) return;
    if (this.inFlight) { this.skipped++; return; }
    this.inFlight = true;
    const generation = this.generation;
    const started = this.options.clock.monotonicMs();
    try {
      const report = await this.options.read();
      if (!this.running || generation !== this.generation || !this.options.isCurrent()) return;
      const received = this.options.clock.monotonicMs();
      const productionTick = this.productionAt === undefined || received - this.productionAt >= 1_000;
      const sample: HostStatsSample = { target: { ...this.target }, ...this.context, report,
        pollStartedMonotonicMs: started, receivedMonotonicMs: received, wallAnchorMs: this.options.clock.wallMs(),
        readDurationMs: Math.max(0, received - started), skippedReads: this.skipped, productionTick };
      if (productionTick) { this.productionAt = received; this.options.production(report); }
      for (const listener of this.listeners) {
        try { listener(Object.freeze(sample)); } catch (error) { this.options.onError?.(error); }
      }
    } catch (error) { this.options.onError?.(error); }
    finally { this.inFlight = false; }
  }

  private schedule(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = setInterval(() => void this.poll(), this.interval);
  }
}
